import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { ArrowLeft, Loader2, TrendingUp } from "lucide-react";

const AREA_LABEL = { direzione: "Direzione", backoffice: "Backoffice", crediti: "Crediti", marketing: "Marketing" };
const BAND_COLORS = { ALTO: "#EF4444", MEDIO: "#F59E0B", BASSO: "#10B981" };
const STATUS_LABEL = { open: "Aperto", paused: "In pausa", closed: "Chiuso" };

const fmtDur = (s) => (s == null ? "—" : s >= 60 ? `${Math.round(s / 60)} min` : `${s}s`);
const pct = (n, tot) => (tot ? `${Math.round((n / tot) * 100)}%` : "0%");

export default function SuperAdminDiscoveryCompare() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const idA = params.get("a");
  const idB = params.get("b");
  const [pair, setPair] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!idA || !idB) { setFailed(true); return; }
    Promise.all([
      api.get(`/discovery/assessments/${idA}/stats`),
      api.get(`/discovery/assessments/${idB}/stats`),
    ])
      .then(([ra, rb]) => setPair([ra.data, rb.data]))
      .catch(() => setFailed(true));
  }, [idA, idB]);

  if (failed) {
    return (
      <div className="py-16 text-center text-sm text-text-muted" data-testid="compare-error">
        Impossibile caricare il confronto: seleziona due assessment validi.
      </div>
    );
  }
  if (!pair) {
    return <div className="py-16 text-center text-text-muted"><Loader2 className="animate-spin mx-auto" size={20} /></div>;
  }

  const [A, B] = pair;
  const ia = A.index.avg;
  const ib = B.index.avg;
  const winner = ia != null && ib != null && ia !== ib ? (ia > ib ? A : B) : null;
  const totA = A.kpi.n_responses;
  const totB = B.kpi.n_responses;

  const rows = [
    { label: "Risposte", va: totA, vb: totB },
    {
      label: "Contatti raccolti",
      va: `${A.kpi.n_contacts} (${Math.round(A.kpi.contact_rate * 100)}%)`,
      vb: `${B.kpi.n_contacts} (${Math.round(B.kpi.contact_rate * 100)}%)`,
    },
    { label: "Durata media", va: fmtDur(A.kpi.avg_duration_s), vb: fmtDur(B.kpi.avg_duration_s) },
    {
      label: "Indice di manualità medio",
      va: ia ?? "—", vb: ib ?? "—",
      hlA: winner === A, hlB: winner === B, big: true, testId: "compare-index-row",
    },
    ...["ALTO", "MEDIO", "BASSO"].map((band) => ({
      label: (
        <span className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: BAND_COLORS[band] }} /> Fascia {band}
        </span>
      ),
      key: `band-${band}`,
      va: `${A.index.bands[band]} (${pct(A.index.bands[band], totA)})`,
      vb: `${B.index.bands[band]} (${pct(B.index.bands[band], totB)})`,
    })),
    ...A.by_area.map((xa) => {
      const xb = B.by_area.find((x) => x.area === xa.area) || {};
      const f = (x) => (x.n ? `${x.avg_index} (${x.n} risposte)` : "—");
      return { label: `Indice ${AREA_LABEL[xa.area]}`, key: `area-${xa.area}`, va: f(xa), vb: f(xb) };
    }),
  ];

  return (
    <div className="space-y-6" data-testid="discovery-compare-page">
      <div>
        <button type="button" onClick={() => navigate("/superadmin/discovery")}
                className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-3"
                data-testid="compare-back-btn">
          <ArrowLeft size={15} /> Tutti gli assessment
        </button>
        <h1 className="text-2xl font-semibold text-text-primary">Confronto assessment</h1>
        <p className="text-sm text-text-secondary mt-1">Fianco a fianco: dove c'è più manualità, c'è più margine di automazione.</p>
      </div>

      {winner && (
        <div className="bg-accent-subtle border border-accent/30 rounded-xl px-5 py-4 flex items-center gap-3" data-testid="compare-winner">
          <TrendingUp size={18} className="text-accent-strong shrink-0" />
          <p className="text-sm text-text-primary">
            <b>«{winner.assessment.title}»</b> ha più margine di automazione:
            indice di manualità <b>{Math.max(ia, ib)}</b> contro {Math.min(ia, ib)}.
          </p>
        </div>
      )}

      <div className="bg-surface border border-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="compare-table">
          <thead>
            <tr className="bg-subtle">
              <th className="text-left px-5 py-4 text-xs uppercase tracking-wider text-text-secondary font-medium w-56">Metrica</th>
              {[A, B].map((x, i) => (
                <th key={x.assessment.id} className="text-left px-5 py-4" data-testid={`compare-head-${i === 0 ? "a" : "b"}`}>
                  <button type="button" onClick={() => navigate(`/superadmin/discovery/${x.assessment.id}`)}
                          className="text-text-primary font-semibold hover:text-accent-strong text-left">
                    {x.assessment.title}
                  </button>
                  <div className="text-[11px] text-text-muted font-normal mt-0.5">
                    {x.assessment.sector || "—"} · {STATUS_LABEL[x.assessment.status] || x.assessment.status}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={r.key || r.label} className="border-t border-border" data-testid={r.testId || `compare-row-${idx}`}>
                <td className="px-5 py-3 text-text-secondary">{r.label}</td>
                {[["va", "hlA"], ["vb", "hlB"]].map(([vk, hk]) => (
                  <td key={vk} className={`px-5 py-3 ${r[hk] ? "bg-accent-subtle" : ""}`}>
                    <span className={`${r.big ? "text-lg font-semibold" : "font-medium"} ${r[hk] ? "text-accent-strong" : "text-text-primary"}`}>
                      {r[vk]}
                    </span>
                    {r[hk] && <span className="ml-2 text-[11px] text-accent-strong">← più margine</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
